import { authExchange } from "@urql/exchange-auth";
import {
  CombinedError,
  createClient,
  errorExchange,
  fetchExchange,
} from "urql";
import { cacheExchange } from "@urql/exchange-graphcache";
import { relayPagination } from "@urql/exchange-graphcache/extras";
import { requestPolicyExchange } from "@urql/exchange-request-policy";
import schema from "../../graphql/schema.json";
import OktaAuth from "@okta/okta-auth-js";

const applicationClient = (oktaAuth: OktaAuth) => {
  return createClient({
    url: process.env.REACT_APP_API_URL!,
    exchanges: [
      cacheExchange({
        schema,
        resolvers: {
          Query: {
            accounts: relayPagination(),
            agents: relayPagination(),
            lenders: relayPagination(),
            groups: relayPagination(),
            identities: relayPagination(),
            offices: relayPagination(),
            conversations: relayPagination(),
            participants: relayPagination(),
            messages: relayPagination(),
            groupNotes: relayPagination(),
            mails: relayPagination(),
            referrals: relayPagination(),
            tours: relayPagination(),
            accountPatches: relayPagination(),
            agentPatches: relayPagination(),
            groupSettingPatches: relayPagination(),
            groupPatches: relayPagination(),
            listingPatches: relayPagination(),
            officePatches: relayPagination(),
            savedFilterPatches: relayPagination(),
            tourPatches: relayPagination(),
            leafletDrafts: relayPagination(),
            rmProblemsForResourceType: relayPagination(),
            rmProblemsForResource: relayPagination(),
            rmProblemsForMapping: relayPagination(),
            internalListingBuckets: relayPagination(),
          },
          CanbyAnalysis: {
            fieldsConnection: relayPagination(),
          },
        },
        keys: {
          AgentLicense: () => null,
          BilledExternalToStripe: () => null,
          CanbyBooleanFieldStatistics: () => null,
          CanbyDateFieldStatistics: () => null,
          CanbyFieldKeyword: () => null,
          CanbyFieldStatistics: () => null,
          CanbyKeywordFieldStatistics: () => null,
          CanbyListFieldStatistics: () => null,
          CanbyNumberFieldStatistics: () => null,
          CanbyObjectFieldStatistics: () => null,
          CanbyRandomSampleIngestType: () => null,
          EnterpriseBillingStripeSubscription: () => null,
          EventHistogram: () => null,
          EventHistogramSummary: () => null,
          Histogram: () => null,
          HistogramSummary: () => null,
          IndividualBilling: () => null,
          ListingAddress: () => null,
          ListingInputDefinitionField: () => null,
          ListingInputDefinitionOrder: () => null,
          ListingInputDefinitionOrderItemField: () => null,
          ListingViewCounts: () => null,
          OfficeAddress: () => null,
          ResourceMetadataExtra: () => null,
          RMJsonPointerMappingSource: () => null,
          SessionCounts: () => null,
          ShowingTimeConfig: () => null,
        },
      }),
      errorExchange({
        onError: (error: CombinedError) => {
          console.error(error);
        },
      }),
      authExchange(async (utils) => {
        return {
          addAuthToOperation: (operation) => {
            try {
              const accessTokenObject = oktaAuth.tokenManager.getSync('accessToken');
              // Access token is stored in the 'accessToken' property for access tokens
              const accessToken = accessTokenObject && 'accessToken' in accessTokenObject ? (accessTokenObject as unknown as { accessToken: string }).accessToken : null;

              const idTokenObject = oktaAuth.tokenManager.getSync('idToken');
              const idToken = idTokenObject && 'idToken' in idTokenObject ? (idTokenObject as unknown as { idToken: string }).idToken : null;

              if (!accessToken) return operation;
              const headers: Record<string, string> = {
                Authorization: `Bearer ${accessToken}`,
              }

              if (idToken) {
                headers['id-token'] = idToken;
              }

              return utils.appendHeaders(operation, headers);
            } catch (error) {
              console.warn('Failed to get access token:', error);
              return operation;
            }
          },
          didAuthError: (error) => {
            return error.graphQLErrors.some(
              (e) =>
                e.extensions?.type === "TokenExpired" ||
                e.extensions?.type === "InvalidToken" ||
                e.extensions?.type === "Denied",
            );
          },
          refreshAuth: async () => {
            // If not authenticated yet, let AuthRequire handle the redirect (it knows originalUri)
            const isAuth = await oktaAuth.isAuthenticated();
            if (!isAuth) {
              return;
            }

            try {
              // Try to renew the token using Okta's token renewal
              const tokenManager = oktaAuth.tokenManager;

              // Check if we have a refresh token
              const refreshToken = await tokenManager.get('refreshToken');
              if (refreshToken) {
                // Attempt to refresh the access token
                await tokenManager.renew('accessToken');
                console.log('Token successfully refreshed using refresh token');
                return;
              }

              // Check if we have an ID token that can be used to get new tokens
              const idToken = await tokenManager.get('idToken');
              if (idToken) {
                try {
                  // Try to get new tokens using the existing session
                  await oktaAuth.token.getWithoutPrompt();
                  console.log('Tokens refreshed using existing session');
                  return;
                } catch (sessionError) {
                  console.log('Silent token refresh failed, checking session');
                }
              }

              // If no refresh token, check if we have an active Okta session
              // This handles cases where Okta has valid session but local tokens expired
              const isAuthenticated = await oktaAuth.isAuthenticated();
              if (isAuthenticated) {
                console.log('Using existing Okta session');
                return;
              }

              // If all else fails, redirect to login
              console.log('Token refresh failed, redirecting to login');
              await oktaAuth.signInWithRedirect();
            } catch (error) {
              console.error('Error during token refresh:', error);

              // Check if we can still get tokens from an active session
              try {
                const isAuthenticated = await oktaAuth.isAuthenticated();
                if (isAuthenticated) {
                  console.log('Recovered using existing session after error');
                  return;
                }
              } catch (sessionError) {
                console.error('Session check failed:', sessionError);
              }

              // If all recovery attempts fail, redirect to login
              console.log('All token recovery attempts failed, redirecting to login');
              await oktaAuth.signInWithRedirect();
            }
          },
          willAuthError: () => {
            try {
              const accessToken = oktaAuth.tokenManager.getSync('accessToken');

              if (!accessToken) {
                return true;
              }

              // Check if token is close to expiration (within 5 minutes for proactive refresh)
              const now = Math.floor(Date.now() / 1000);
              const expiresAt = accessToken.expiresAt;
              const timeUntilExpiration = expiresAt - now;

              return timeUntilExpiration <= 300; // 5 minutes
            } catch (error) {
              console.warn('Error checking token expiration:', error);
              return true;
            }
          },
        };
      }),
      requestPolicyExchange({}),
      fetchExchange,
    ],
  });
};

export default applicationClient;